import React, {FunctionComponent, useEffect, useMemo} from 'react'
import {useRootDispatch, useRootSelector} from '../../../utils/hooks'
import styles from './StatisticsPage.module.sass'
import RequestDropdown from '../../data-components/dropdowns/RequestDropdown/RequestDropdown'
import {User} from '../../../responses/users'
import {apiRequests} from '../../../http/config'
import Button from '../../controls/Button/Button'
import DownloadIcon from 'files/download.svg'
import clsx from 'clsx'
import {Classes} from '../../../styles'
import Spacer from '../../etc/Spacer'
import {DepartmentWithUsers} from '../../../responses/departments'
import {flow, pipe} from 'fp-ts/function'
import {WorkObject} from '../../../responses/objects'
import StatisticsTable from './StatisticsTable/StatisticsTable'
import {ColumnConfiguration} from './StatisticsTable/configuration'
import {ByObjectData} from '../../../responses/statistics'
import * as O from 'fp-ts/Option'
import {byObjectActions} from '../../../redux/pages/statistics/byObject'
import {entityArrayRemove, entityElem, entityToggle, eqEntity, getId} from '../../../utils/entity-utils'
//import {toBool} from '../../../utils/utils'
import {toServerDate} from '../../../utils/date-utils'
import {fst, snd} from 'fp-ts/Tuple'
import ColumnPicker from './ColumnPicker/ColumnPicker'
import {NonEmptyArray} from 'fp-ts/NonEmptyArray'
import FiltersClearIcon from '../../../../files/filters-clear.svg'
import {ListResponse, Query, RequestStatus} from '../../../responses/response'
import TopBarProgress from 'react-topbar-progress-indicator'
import {isDefinedTuple} from '../../../utils/utils'
import * as A from 'fp-ts/Array'
import MultiLevelDropdown from '../../data-components/dropdowns/TwoLevelDropdown/MultiLevelDropdown'
import {useObservableState} from 'observable-hooks'
import {fromQueryWithReset} from '../../../utils/rx-operators'
import {Observable} from 'rxjs'
import {TypedAjaxResponse} from '../../../http/types'

const configuration: NonEmptyArray<ColumnConfiguration<ByObjectData>> = [{
    group: '',
    name: '',
    render: data => data.name,
},{
    group: 'Тендеры',
    name: 'Выиграно',
    render: data => data.total.project_work_objects.tenders.won ?? 0,
}, {
    group: 'Тендеры',
    name: 'Проиграно',
    render: data => data.total.project_work_objects.tenders.lost ?? 0,
}, {
    group: 'Тендеры',
    name: 'Отменено',
    render: data => data.total.project_work_objects.tenders.canceled ?? 0,
}, {
    group: 'Дизайн',
    name: 'Выиграно',
    render: data => data.total.project_work_objects.design.won ?? 0,
}, {
    group: 'Дизайн',
    name: 'Проиграно',
    render: data => data.total.project_work_objects.design.lost ?? 0,
}, {
    group: 'Дизайн',
    name: 'Отменено',
    render: data => data.total.project_work_objects.design.canceled ?? 0,
}, {
    group: 'Производство',
    name: 'Выиграно',
    render: data => data.total.project_work_objects.production.won ?? 0,
}, {
    group: 'Производство',
    name: 'Проиграно',
    render: data => data.total.project_work_objects.production.lost ?? 0,
}, {
    group: 'Производство',
    name: 'Отменено',
    render: data => data.total.project_work_objects.production.canceled ?? 0,
}]

const departmentsRequest = (query: Query) => (apiRequests.dictionary.list({
    query: {
        ...query,
        with_users: true,
    },
    params: {key: 'department'}
}) as Observable<TypedAjaxResponse<ListResponse<DepartmentWithUsers>>>)
const ObjectTab: FunctionComponent = () => {
    const dispatch = useRootDispatch()

    const {departments, users, managers, workObjects, data, hide, open, sort, state} = useRootSelector(state => state.pages.statistics.byObject)
    const { brands, clients, date} = useRootSelector(state => state.pages.statistics.byClient)
    const [department, getDepartment] = useObservableState<DepartmentWithUsers[], { search: string, reset: boolean }>(fromQueryWithReset(departmentsRequest), [])
    const selectedDepartments = useMemo(() =>
            department.filter(department => department.users.length > 0 && department.users.every(user => entityElem(user, users))),
        [department, users]
    )

    useEffect(() => {
        if (isDefinedTuple(date)) {
            dispatch(byObjectActions.data.request({
                work_objects: workObjects.map(getId),
                departments: departments.map(getId),
                managers: managers.map(getId),
                users: users.map(getId),
                brands: brands.map(getId),
                clients: clients.map(getId),
                date_from: toServerDate(fst(date)),
                date_to: toServerDate(snd(date)),
            }))
        }
    }, [dispatch, workObjects, departments, managers, users, brands, clients, date])


    return <div className={styles.content}>
        {
            state === RequestStatus.FETCHING && <TopBarProgress/>
        }
        <div className={styles.header}>
            <MultiLevelDropdown<DepartmentWithUsers, User>
                data={department}
                request={getDepartment}
                levels={2}
                selection={[selectedDepartments, users]}
                selectors={[department => department.users]}
                renders={[department => department.title, user => user.name]}
                className={styles.dropdown}
                placeholder={'Отдел/Исполнитель'}
                onChange={data => {
                    switch (data.level) {
                        case 0:
                            if (data.selected) {
                                dispatch(byObjectActions.setUsers(pipe(
                                    users,
                                    A.concat(data.item.users as User[]),
                                    A.uniq<User>(eqEntity),
                                )))
                            } else {
                                dispatch(byObjectActions.setUsers(pipe(
                                    users,
                                    entityArrayRemove(data.item.users as User[])
                                )))
                            }
                            break
                        case 1:
                            dispatch(byObjectActions.setUsers(pipe(
                                users,
                                entityToggle(data.item),
                            )))
                            break
                    }
                }}
            />
            <RequestDropdown<WorkObject>
                className={styles.dropdown}
                placeholder={'Объект'}
                type={'multi'}
                value={workObjects}
                onChange={workObjects=> dispatch(byObjectActions.setWorkObjects(workObjects))}
                request={query => apiRequests.dictionary.list({
                    params: {key: 'work-object'},
                    query
                })}
                render={workObjects => workObjects.name}
            />
            <RequestDropdown<User>
                className={styles.dropdown}
                placeholder={'Менеджер'}
                type={'multi'}
                value={managers}
                onChange={managers=> dispatch(byObjectActions.setManagers(managers))}
                request={query => apiRequests.dictionary.list({
                    params: {key: 'manager'},
                    query
                })}
                render={user => user.name}
            />
            <Button
                title={'Очистить фильтры'}
                onClick={() => dispatch(byObjectActions.clearFilters())}
            >
                <FiltersClearIcon/>
            </Button>
            <Spacer/>
            <div className={clsx(styles.text ,Classes.f9)}>
                <div>Для выгрузки в Excel выберите столбцы,</div>
                <div>Менеджера и соответствующих Исполнителей.</div>
            </div>
            <ColumnPicker hide={hide} config={configuration} onChange={flow(byObjectActions.setHiddenColumns, dispatch)}/>
            <Button title={'Скачать в формате Excel'}>
                <DownloadIcon />
            </Button>
        </div>
        {
            O.isSome(data) && <StatisticsTable
                name={'Исполнитель'}
                data={data.value}
                config={configuration}
                hide={hide}
                open={open}
                sort={sort}
                onSortChange={flow(byObjectActions.setSort, dispatch)}
                onRowToggle={flow(byObjectActions.toggleRow, dispatch)}
            />
        }
    </div>
}

export default ObjectTab